import React, { useContext } from "react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import tourInclusionsPlusIcon from "../../icons/tourInclusionsPlusIcon.svg";
import tourExclusionsMinusIcon from "../../icons/tourExclusionsMinusIcon.svg";
import { TranslationContext, StyleTypeContext } from "../../../umbilical_tools";
import jssVariables from "../../../jss_setup";
import { InlineIcon } from "@iconify/react";

const incExcStyles = createUseStyles({
  rules: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    margin: [
      jssVariables.double_gap,
      jssVariables.spacing.center_margin_mobile_medium[1],
    ],
    ..._.get(props, "customStyles.incExcRules", {}),
  }),
  list: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    alignContent: "start",
    ..._.get(props, "customStyles.incExcRuleList", {}),
  }),
  header: (props) => ({
    fontSize: "1.76rem",
    fontWeight: "700",
    ..._.get(props, "customStyles.incExcRuleHeader", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    rules: (props) => ({
      margin: [
        jssVariables.navbar_height,
        jssVariables.spacing.center_margin_large[1],
      ],
      gridTemplateColumns: "repeat(2, 1fr)",
      ..._.get(props, "customStyles.incExcRules.smallscreen", {}),
    }),
    list: (props) => ({
      ..._.get(props, "customStyles.incExcRuleList.smallscreen", {}),
    }),
  },
});
const InclusionsExclusions = (props) => {
  const { details, customStyles } = props;
  const { t } = useContext(TranslationContext);

  const classes = incExcStyles({
    customStyles,
  });

  var inclusions = [];
  var exclusions = [];
  if (_.get(details, "product_type") === "PKG") {
    inclusions = _.get(details, "inclusions_exclusions.inclusions", []).filter(
      (rule) => rule.name
    );

    exclusions = _.get(details, "inclusions_exclusions.exclusions", []).filter(
      (rule) => rule.name
    );
  } else {
    inclusions = _.get(details, "inclusions", []).filter((rule) => rule.name);
    exclusions = _.get(details, "exclusions", []).filter((rule) => rule.name);
  }

  return (
    <div className={classes.rules}>
      {inclusions.length ? (
        <div className={classes.list}>
          <h3 className={classes.header}>{t("inclusions")}</h3>
          {inclusions
            .filter((rule) => rule.name)
            .map((rule, idx) => (
              <Inclusion key={idx} rule={rule} customStyles={customStyles} />
            ))}
        </div>
      ) : null}
      {exclusions.length ? (
        <div className={classes.list}>
          <h3 className={classes.header}>{t("exclusions")}</h3>
          {exclusions
            .filter((rule) => rule.name)
            .map((rule, idx) => (
              <Exclusion key={idx} rule={rule} customStyles={customStyles} />
            ))}
        </div>
      ) : null}
    </div>
  );
};
InclusionsExclusions.defaultProps = {
  details: {},
  customStyles: {},
};
InclusionsExclusions.propTypes = {
  details: PropTypes.object.isRequired,
  customStyles: PropTypes.object.isRequired,
};
export default InclusionsExclusions;

const ruleStyles = {
  display: "grid",
  gridGap: jssVariables.half_gap,
  background: "rgba(0, 0, 0, 0) !important",
  color: jssVariables.colors.text.dark,
  borderBottom: `2px solid ${jssVariables.colors.base_color}`,
  paddingBottom: jssVariables.normal_gap,
};

const ruleHeaderStyles = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  gridGap: `calc(${jssVariables.normal_gap} / 2)`,
  alignItems: "center",
  fontWeight: "100",
  textTransform: "capitalize",
};

const ruleDescStyles = {
  padding: [0, jssVariables.half_gap],
};

const incExcRuleStyles = createUseStyles({
  rule: (props) => ({
    ...ruleStyles,
    ..._.get(props, "customStyles.incExcRule", {}),
  }),
  header: (props) => ({
    ...ruleHeaderStyles,
    fontWeight: "normal",
    ..._.get(props, "customStyles.incExcHeader", {}),
  }),
  description: (props) => ({
    ...ruleDescStyles,
    ..._.get(props, "customStyles.description", {}),
  }),
  avatar: (props) => ({
    fontSize: "xx-large",
    borderRadius: "50%",
    border: "4px solid green",
    color: "green",
    ..._.get(props, "customStyles.avatar", {}),
  }),
});
const Inclusion = (props) => {
  const { rule, customStyles } = props;
  const { type } = useContext(StyleTypeContext);

  const classes = incExcRuleStyles({
    customStyles,
  });
  return (
    <div className={classes.rule}>
      <h5 className={classes.header}>
        {type === "type2" ? (
          <img className={classes.logo} alt="" src={tourInclusionsPlusIcon} />
        ) : (
          <InlineIcon
            icon="fluent-mdl2:skype-check"
            className={classes.avatar}
          />
        )}

        {rule.name_en || rule.name}
      </h5>
      {rule.description_en ? (
        <span className={classes.description}>{rule.description_en}</span>
      ) : null}
    </div>
  );
};
Inclusion.defaultProps = {
  rule: {},
  customStyles: {},
};
Inclusion.propTypes = {
  rule: PropTypes.object.isRequired,
  customStyles: PropTypes.object.isRequired,
};

const excRuleStyles = createUseStyles({
  rule: (props) => ({
    ...ruleStyles,
    ..._.get(props, "customStyles.incExcRule", {}),
  }),
  header: (props) => ({
    ...ruleHeaderStyles,
    ..._.get(props, "customStyles.incExcHeader", {}),
  }),
  description: (props) => ({
    ...ruleDescStyles.padding,
    ..._.get(props, "customStyles.description", {}),
  }),
  avatar: (props) => ({
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    border: "4px solid red",
    color: "red",
    padding: "0.25rem",
    ..._.get(props, "customStyles.avatar", {}),
  }),
});
const Exclusion = (props) => {
  const { rule, customStyles } = props;
  const { type } = useContext(StyleTypeContext);

  const classes = excRuleStyles({
    customStyles,
  });
  return (
    <div className={classes.rule}>
      <h5 className={classes.header}>
        {type === "type2" ? (
          <img className={classes.logo} alt="" src={tourExclusionsMinusIcon} />
        ) : (
          <InlineIcon icon="fa:close" className={classes.avatar} />
        )}
        {rule.name_en || rule.name}
      </h5>
      {rule.description_en ? (
        <span className={classes.description}>{rule.description_en}</span>
      ) : null}
    </div>
  );
};
Exclusion.defaultProps = {
  rule: {},
  customStyles: {},
};
Exclusion.propTypes = {
  rule: PropTypes.object.isRequired,
  customStyles: PropTypes.object.isRequired,
};
